import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { Download, Users, DollarSign, Calendar, TrendingUp } from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/Table';
import { payrollService } from '../../services/payroll.service';
import { PayrollRecord, PayrollStatus } from '../../types';

export const PayrollReportTab: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedMonth, setSelectedMonth] = useState<number | null>(null);

  const { data: yearlyPayroll, isLoading: loadingYearly } = useQuery({
    queryKey: ['payroll', 'yearly', selectedYear],
    queryFn: () => payrollService.getByYear(selectedYear),
  });

  const { data: monthlyPayroll, isLoading: loadingMonthly } = useQuery({
    queryKey: ['payroll', 'monthly', selectedYear, selectedMonth],
    queryFn: () => payrollService.getByPeriod(selectedYear, selectedMonth!),
    enabled: selectedMonth !== null,
  });

  const payrollList = useMemo<PayrollRecord[]>(() => {
    if (selectedMonth !== null) {
      return monthlyPayroll || [];
    }
    return yearlyPayroll || [];
  }, [yearlyPayroll, monthlyPayroll, selectedMonth]);

  const isLoading = loadingYearly || (selectedMonth !== null && loadingMonthly);

  // Calculate statistics
  const stats = useMemo(() => {
    const totalBasicSalary = payrollList.reduce((sum, p) => sum + (p.basicSalary || 0), 0);
    const totalAllowances = payrollList.reduce((sum, p) => sum + (p.totalAllowances || 0), 0);
    const totalDeductions = payrollList.reduce((sum, p) => sum + (p.totalDeductions || 0), 0);
    const totalGrossPay = payrollList.reduce((sum, p) => sum + (p.grossPay || 0), 0);
    const totalNetPay = payrollList.reduce((sum, p) => sum + (p.netPay || 0), 0);
    
    const byStatus = {
      draft: payrollList.filter(p => p.status === PayrollStatus.DRAFT).length,
      pending: payrollList.filter(p => p.status === PayrollStatus.PENDING_APPROVAL).length,
      approved: payrollList.filter(p => p.status === PayrollStatus.APPROVED).length,
      paid: payrollList.filter(p => p.status === PayrollStatus.PAID).length,
      cancelled: payrollList.filter(p => p.status === PayrollStatus.CANCELLED).length,
    };

    // Group by month for chart data
    const byMonth: Record<number, number> = {};
    payrollList.forEach(p => {
      const month = p.payrollMonth;
      byMonth[month] = (byMonth[month] || 0) + p.netPay;
    });

    return {
      totalBasicSalary,
      totalAllowances,
      totalDeductions,
      totalGrossPay,
      totalNetPay,
      totalRecords: payrollList.length,
      byStatus,
      byMonth,
    };
  }, [payrollList]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-TZ', { style: 'currency', currency: 'TZS', maximumFractionDigits: 0 }).format(value);
  };

  const getStatusVariant = (status: PayrollStatus) => {
    switch (status) {
      case PayrollStatus.PAID: return 'success';
      case PayrollStatus.APPROVED: return 'info';
      case PayrollStatus.PENDING_APPROVAL: return 'warning';
      case PayrollStatus.DRAFT: return 'default';
      case PayrollStatus.CANCELLED: return 'error';
      default: return 'default';
    }
  };

  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' },
  ];

  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  const exportToCSV = () => {
    const headers = ['Payroll #', 'Employee', 'Period', 'Basic Salary', 'Allowances', 'Deductions', 'Gross Pay', 'Net Pay', 'Status'];
    const rows = payrollList.map(p => [
      p.payrollNumber,
      p.userName,
      p.payrollPeriod,
      p.basicSalary,
      p.totalAllowances,
      p.totalDeductions,
      p.grossPay,
      p.netPay,
      p.status,
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `payroll_report_${selectedYear}${selectedMonth ? `_${selectedMonth}` : ''}.csv`;
    link.click();
  };

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          >
            {years.map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Month</label>
          <select
            value={selectedMonth || ''}
            onChange={(e) => setSelectedMonth(e.target.value ? Number(e.target.value) : null)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="">All Months</option>
            {months.map(month => (
              <option key={month.value} value={month.value}>{month.label}</option>
            ))}
          </select>
        </div>
        <div className="flex-1" />
        <Button variant="secondary" onClick={exportToCSV} disabled={payrollList.length === 0}>
          <Download size={16} className="mr-2" />
          Export CSV
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-100 rounded-lg">
              <Users size={24} className="text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Records</p>
              <p className="text-2xl font-bold">{stats.totalRecords}</p>
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center gap-3">
            <div className="p-3 bg-green-100 rounded-lg">
              <DollarSign size={24} className="text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Net Pay</p>
              <p className="text-xl font-bold">{formatCurrency(stats.totalNetPay)}</p>
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center gap-3">
            <div className="p-3 bg-purple-100 rounded-lg">
              <TrendingUp size={24} className="text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Allowances</p>
              <p className="text-xl font-bold">{formatCurrency(stats.totalAllowances)}</p>
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center gap-3">
            <div className="p-3 bg-orange-100 rounded-lg">
              <Calendar size={24} className="text-orange-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Deductions</p>
              <p className="text-xl font-bold">{formatCurrency(stats.totalDeductions)}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Status Breakdown */}
      <Card title="Status Breakdown">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <p className="text-2xl font-bold text-gray-600">{stats.byStatus.draft}</p>
            <p className="text-sm text-gray-500">Draft</p>
          </div>
          <div className="text-center p-4 bg-yellow-50 rounded-lg">
            <p className="text-2xl font-bold text-yellow-600">{stats.byStatus.pending}</p>
            <p className="text-sm text-yellow-700">Pending Approval</p>
          </div>
          <div className="text-center p-4 bg-blue-50 rounded-lg">
            <p className="text-2xl font-bold text-blue-600">{stats.byStatus.approved}</p>
            <p className="text-sm text-blue-700">Approved</p>
          </div>
          <div className="text-center p-4 bg-green-50 rounded-lg">
            <p className="text-2xl font-bold text-green-600">{stats.byStatus.paid}</p>
            <p className="text-sm text-green-700">Paid</p>
          </div>
          <div className="text-center p-4 bg-red-50 rounded-lg">
            <p className="text-2xl font-bold text-red-600">{stats.byStatus.cancelled}</p>
            <p className="text-sm text-red-700">Cancelled</p>
          </div>
        </div>
      </Card>

      {/* Monthly Breakdown (only for yearly view) */}
      {selectedMonth === null && Object.keys(stats.byMonth).length > 0 && (
        <Card title="Monthly Net Pay Distribution">
          <div className="grid grid-cols-3 md:grid-cols-6 lg:grid-cols-12 gap-2">
            {months.map(month => (
              <div key={month.value} className="text-center p-2 bg-gray-50 rounded">
                <p className="text-xs text-gray-500">{month.label.substring(0, 3)}</p>
                <p className="text-sm font-semibold">
                  {stats.byMonth[month.value] ? formatCurrency(stats.byMonth[month.value]).replace('TZS', '').trim() : '-'}
                </p>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Payroll Records Table */}
      <Card title="Payroll Records">
        {isLoading ? (
          <div className="text-center py-8 text-gray-500">Loading payroll data...</div>
        ) : payrollList.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No payroll records found for {selectedMonth ? `${months.find(m => m.value === selectedMonth)?.label} ` : ''}{selectedYear}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Payroll #</TableHead>
                  <TableHead>Employee</TableHead>
                  <TableHead>Period</TableHead>
                  <TableHead className="text-right">Basic Salary</TableHead>
                  <TableHead className="text-right">Allowances</TableHead>
                  <TableHead className="text-right">Deductions</TableHead>
                  <TableHead className="text-right">Gross Pay</TableHead>
                  <TableHead className="text-right">Net Pay</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payrollList.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell className="font-medium">{p.payrollNumber}</TableCell>
                    <TableCell>{p.userName}</TableCell>
                    <TableCell>{p.payrollPeriod}</TableCell>
                    <TableCell className="text-right">{formatCurrency(p.basicSalary)}</TableCell>
                    <TableCell className="text-right text-green-600">+{formatCurrency(p.totalAllowances)}</TableCell>
                    <TableCell className="text-right text-red-600">-{formatCurrency(p.totalDeductions)}</TableCell>
                    <TableCell className="text-right">{formatCurrency(p.grossPay)}</TableCell>
                    <TableCell className="text-right font-semibold">{formatCurrency(p.netPay)}</TableCell>
                    <TableCell>
                      <Badge variant={getStatusVariant(p.status)}>{p.status.replace('_', ' ')}</Badge>
                    </TableCell>
                  </TableRow>
                ))}
                {/* Totals Row */}
                <TableRow className="bg-gray-50 font-semibold">
                  <TableCell colSpan={3}>Totals</TableCell>
                  <TableCell className="text-right">{formatCurrency(stats.totalBasicSalary)}</TableCell>
                  <TableCell className="text-right text-green-600">+{formatCurrency(stats.totalAllowances)}</TableCell>
                  <TableCell className="text-right text-red-600">-{formatCurrency(stats.totalDeductions)}</TableCell>
                  <TableCell className="text-right">{formatCurrency(stats.totalGrossPay)}</TableCell>
                  <TableCell className="text-right">{formatCurrency(stats.totalNetPay)}</TableCell>
                  <TableCell>{' '}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        )}
      </Card>
    </div>
  );
};
